import React, {useCallback, useEffect, useState} from 'react';
import {Button, Col, Layout, Menu, Row, Drawer, Space} from 'antd';
import {Link, useNavigate, useRoutes, useLocation} from "react-router-dom";
import {isMobile} from "react-device-detect";
import routes from "../../routes";
import './style.css';
import {
    CheckOutlined,
    CloseOutlined, EuroOutlined,
    HomeFilled, IdcardFilled,
    LogoutOutlined, MenuOutlined, OrderedListOutlined, SearchOutlined, ShoppingCartOutlined,
    SoundFilled, StopFilled,
    TeamOutlined, UsergroupAddOutlined,
    UserOutlined
} from "@ant-design/icons";
import {CropIcon} from "../../Icons";
import api from "../../api";
import dayjs from "dayjs";
import HeaderHelpLink from "../HeaderHelpLink";
import {useTranslations} from "../../lang";
import {useAuth} from "../../auth";
import {DEFAULT_GUTTER} from "../../constants";
import FeedbackModal from "../FeedbackModal";
import {safeStorage} from "../../errorReporting";

const {Header, Content, Sider} = Layout;


const AppLayout = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const routingElement = useRoutes(routes)
    const {translate} = useTranslations()
    const {company, plan, isIos} = useAuth();
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [errorContext, setErrorContext] = useState(null);

    const handleApiError = useCallback((e) => {
        setErrorContext(e.detail);
        setFeedbackOpen(true);
    }, []);

    useEffect(() => {
        window.addEventListener('api-error', handleApiError);
        return () => window.removeEventListener('api-error', handleApiError);
    }, [handleApiError]);

    let isExpired = false

    const goToPage = (e) => {
        navigate(e.key)
        if (isMobile) {
            handleOnCollapse(true ? "true" : "false")
        }
    }

    let menuItems = [
        {
            key: 'gardens',
            label: translate('Tuinen'),
            onClick: goToPage,
            icon: <HomeFilled/>
        },
        {
            key: 'crops',
            label: translate('Mijn Gewassen'),
            onClick: goToPage,
            icon: <CropIcon/>
        },
        {
            key: 'products',
            label: translate('Gewassen Database'),
            onClick: goToPage,
            icon: <SearchOutlined/>
        }
    ]

    if (!isIos) {
        menuItems.push({
            key: 'profile',
            label: translate('Publiek profiel'),
            onClick: goToPage,
            icon: <IdcardFilled/>
        })
        menuItems.push({
            key: 'updates',
            label: translate('Updates'),
            onClick: goToPage,
            icon: <SoundFilled/>
        })

        if (plan.has_team) {
            menuItems.push({
                key: 'team',
                label: translate('Mijn Team'),
                onClick: goToPage,
                icon: <TeamOutlined/>
            })
        }
    }

    menuItems.push({
        key: 'tasks',
        label: translate('Taken'),
        onClick: goToPage,
        icon: <CheckOutlined/>
    })

    // menuItems.push({
    //     key: 'auto-planner',
    //     label: translate('Auto Planner'),
    //     onClick: goToPage,
    //     icon: <OrderedListOutlined/>
    // })

    menuItems.push({
        key: 'community',
        label: 'Community',
        onClick: () => window.open('https://www.skool.com/moestuin-4621', '_blank'),
        icon: <UsergroupAddOutlined/>
    })
    //
    // menuItems.push({
    //     key: 'cropplan',
    //     label: translate('Teelplan'),
    //     onClick: goToPage,
    //     icon: <OrderedListOutlined/>
    // })

    if (company.plan_expires_at) {
        const dateToCheck = dayjs(company.plan_expires_at); // Example date
        const now = dayjs(); // Current date and time

        if (dateToCheck.isBefore(now)) {
            menuItems = []
            isExpired = true
        }
    }

    if (plan.has_selling) {
        menuItems.push({
            key: 'offers',
            label: translate('Aanbod'),
            onClick: goToPage,
            icon: <ShoppingCartOutlined/>
        })

        menuItems.push({
            key: 'orders',
            label: translate('Bestellingen'),
            onClick: goToPage,
            icon: <EuroOutlined/>
        })
    }


    if (api.isAdmin()) {

        menuItems.push({
            key: 'admin',
            label: 'Admin',
            onClick: goToPage,
            icon: <UsergroupAddOutlined/>
        })

        menuItems.push({
            key: 'admin/translations',
            label: 'Translations',
            onClick: goToPage,
            icon: <SearchOutlined/>
        })

    }

    if (safeStorage.get('admin_token')) {
        menuItems.push({
            key: 'admin/impersonate/stop',
            label: 'Back to admin',
            onClick: goToPage,
            icon: <StopFilled/>
        })
    }


    if (!isIos) {
        menuItems.push({
            key: 'account',
            label: translate('Mijn Account'),
            onClick: goToPage,
            icon: <UserOutlined/>
        })
    } else {

        menuItems.push({
            key: 'account-ios',
            label: translate('Mijn Account'),
            onClick: goToPage,
            icon: <UserOutlined/>
        })
    }

    menuItems.push({
        key: 'logout',
        label: translate('Uitloggen'),
        onClick: goToPage,
        icon: <LogoutOutlined/>
    })

    const menuClosed = safeStorage.get('menuClosed')
    // Default to open on desktop (false = open), closed on mobile (true = closed)
    const defaultCollapsed = (menuClosed === "true" ? true : false)

    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const isLoggedIn = api.isLoggedIn()

    function handleOnCollapse(value) {
        safeStorage.set('menuClosed', value)
        setCollapsed(value)
    }

    const handleMobileMenuClick = (e) => {
        navigate(e.key)
        setMobileDrawerOpen(false) // Close drawer after navigation
    }

    const isLandingPage = location.pathname === '/'
    const isAffiliateRegisterPage = location.pathname === '/affiliate-aanmelden'

    return (


        <Layout
            style={{
                minHeight: '100vh',
            }}
        >
            {/* Desktop Sidebar - Hidden on mobile and landing page */}
            {(isLoggedIn && !isMobile && !isLandingPage && (
                <Sider className={"custom-slider"}
                       collapsedWidth={"40px"}
                       width={"250px"}
                       collapsible
                       collapsed={collapsed}
                       onCollapse={(value) => handleOnCollapse(value)}
                       trigger={null}
                >
                    <div style={{textAlign: 'right', paddingTop: '8px'}}>
                        <Button type={"link"} onClick={() => handleOnCollapse(!collapsed)}>
                            {collapsed ? (<MenuOutlined/>) : <CloseOutlined/>}
                        </Button>
                    </div>
                    <Menu defaultSelectedKeys={['1']} mode="inline" items={menuItems}
                          style={{
                              position: 'sticky',
                              top: 0,
                              zIndex: 1000,
                          }}
                    />
                </Sider>
            ))}

            {/* Mobile Drawer - Hidden on landing page */}
            {(isLoggedIn && isMobile && !isLandingPage && (
                <Drawer
                    title={translate("Menu")}
                    placement="left"
                    onClose={() => setMobileDrawerOpen(false)}
                    open={mobileDrawerOpen}
                    bodyStyle={{ padding: 0 }}
                    zIndex={1001}
                >
                    <Menu
                        mode="inline"
                        items={menuItems.map(item => ({
                            ...item,
                            onClick: handleMobileMenuClick
                        }))}
                        style={{ border: 'none' }}
                    />
                </Drawer>
            ))}
            
            <Layout>

                {/* Hide header on landing page */}

                <Header
                    style={{
                        padding: '0 16px',
                        background: 'white',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                >
                    <Row gutter={DEFAULT_GUTTER} align="middle">
                        {/* Mobile Hamburger Menu */}
                        {(isLoggedIn && isMobile && !isLandingPage && (
                            <Col xs={2}>
                                <Button
                                    type="text"
                                    icon={<MenuOutlined />}
                                    onClick={() => setMobileDrawerOpen(true)}
                                    style={{ marginLeft: '8px' }}
                                />
                            </Col>
                        ))}

                        <Col xs={isLoggedIn && isMobile ? 14 : 12} md={16}>
                            <Link to={"/"}>
                                <img alt={"Logo"} style={{maxHeight: '40px', marginLeft: isMobile && isLoggedIn ? '8px' : '28px'}}
                                     src={"/static/img/logo.png"}/>
                            </Link>
                        </Col>
                        <Col xs={isLoggedIn && isMobile ? 8 : 12} md={8} style={{textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
                            <Space align="center">
                                {!isLoggedIn && !isAffiliateRegisterPage && (
                                    <Button type={"primary"} onClick={() => navigate('/login')}>
                                        <UserOutlined /> {isMobile ? '' : translate('Inloggen')}
                                    </Button>
                                )}

                                {isLoggedIn && isLandingPage && (
                                    <Button type={"primary"} onClick={() => navigate('/gardens')}>
                                        <UserOutlined /> {isMobile ? '' : translate('Open Planner')}
                                    </Button>
                                )}
                                <HeaderHelpLink/>
                            </Space>
                        </Col>
                    </Row>
                </Header>



                <Content>
                    {isExpired && (
                        <div style={{padding: '20px', textAlign: 'center', background: 'white'}}>
                            <h1>{translate('Je abonnement is verlopen of niet actief!')}</h1>
                            <p>{translate('Je kunt nu geen gebruik meer maken van Oogst.app, doe eerst een betaling om verder te gaan.')}</p>
                            <Button onClick={() => navigate('/account')} type={"primary"}>{translate('Verleng je abonnement')}</Button>
                        </div>
                    )}
                    <div
                        style={{
                            padding: location.pathname === '/' ? 0 : 24,
                            minHeight: 360,
                        }}
                    >
                        {routingElement}
                    </div>
                </Content>
            </Layout>
            <FeedbackModal
                open={feedbackOpen}
                onCancel={() => setFeedbackOpen(false)}
                errorContext={errorContext}
            />
        </Layout>

    );
};
export default AppLayout;